/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deviceService } from '@/services/api/services/deviceService';
import { DeviceFormData, VehicleType, DeviceStatus } from '@/types/device.types';
import { DEVICE_FEATURES } from '@/types/features.flags.types';
import { deviceMappers } from '@/types/mappers';
import { usePermissions } from '@/contexts/PermissionsContext';
import { PERMISSIONS } from '@/contexts/PermissionsContext';
import { useToast } from '@/contexts/ToastContext';
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation';
import { deviceTypesConfig, getVehicleTypeLabel } from '@/config/deviceTypes.config';
import { useAuth } from '@/contexts/AuthContext';
import i18n from '@/i18n/i18n';

export const DeviceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { showToast } = useToast();
  const { confirmDelete, showErrorDialog } = useDeleteConfirmation();
  const { user } = useAuth();
  const [formData, setFormData] = useState<DeviceFormData>({
    id: '',
    serialNumber: '',
    alias: '',
    description: '',
    vehicle: {
      plate: '',
      type: '' as VehicleType,
      dimensions: {
        length: 0,
        height: 0,
        maxLoad: 0,
      },
    },
    versions: {
      controller: '',
      screen: '',
      sensor: '',
    },
    status: DeviceStatus.ACTIVE,
    createdAt: new Date(),
    updatedAt: new Date(),
    metadata: {
      version: 1,
      status: 'active',
    },
    nextExpectedSequence: undefined,
  });
  const [sequenceResetOpen, setSequenceResetOpen] = useState(false);
  const [sequenceInput, setSequenceInput] = useState('0');
  const [sequenceSaving, setSequenceSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedVehicleTypeCategory, setSelectedVehicleTypeCategory] = useState<string>('OTHER');
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(true);

  const vehicleTypes: VehicleType[] = useMemo(() => 
    deviceTypesConfig.types.map(type => type.value as VehicleType), 
    []
  );

  // Security validation function
  const validateSecurityAccess = useCallback(async () => {
    if (!user?.id) {
      showToast(t('errors.userNotAuthenticated'), 'error');
      navigate('/devices');
      return;
    }

    try {
      setIsCheckingSecurity(true);
      setError(null);

      // 1. Check if user can access device management (only Administrators and Supervisors)
      if (user.role !== 'Administrador' && user.role !== 'Supervisor') {
        showToast(t('errors.noAccessToDeviceManagement'), 'error');
        navigate('/');
        return;
      }

      // 2. Check if user can create devices (only Administrators)
      if (!id && !hasPermission(PERMISSIONS.DEVICE_CREATE)) {
        showToast(t('errors.onlyAdministratorsCanCreateDevices'), 'error');
        navigate('/devices');
        return;
      }

      // 3. If editing a device, check if user has access to it
      if (id) {
        // For Administrators, they can edit any device
        if (user.role === 'Administrador') {
          setIsCheckingSecurity(false);
          return;
        }

        // For Supervisors, check if they have access to this specific device
        if (user.role === 'Supervisor') {
          const userDevicesResponse = await deviceService.getByUserId(user.id);
          
          if (!userDevicesResponse.success || !userDevicesResponse.data) {
            showToast(t('errors.failedToCheckDeviceAccess'), 'error');
            navigate('/devices');
            return;
          }

          // Check if the device ID is in the user's associated devices
          const hasAccess = userDevicesResponse.data.some(device => device.id === id);
          
          if (!hasAccess) {
            showToast(t('errors.youDontHaveAccessToThisDevice'), 'error');
            navigate('/devices');
            return;
          }
        }
      }

      setIsCheckingSecurity(false);
    } catch (error) {
      console.error('Error validating security access:', error);
      showToast(t('errors.securityValidationFailed'), 'error');
      navigate('/devices');
    }
  }, [user, id, hasPermission, t, showToast, navigate]);

  const validateForm = useCallback((): boolean => {
    if (!isDirty) return true;

    const errors: Record<string, string> = {};

    if (!formData.serialNumber.trim()) {
      errors.serialNumber = t('pages.devices.validation.serialNumberRequired');
    } else if (!/^[A-Z0-9-]{5,50}$/.test(formData.serialNumber)) {
      errors.serialNumber = t('pages.devices.validation.serialNumberFormat');
    }

    // Plate is optional. If provided, must be valid; otherwise skip.
    if (formData.vehicle.plate && !formData.vehicle.plate.trim()) {
      errors.plate = t('pages.devices.validation.plateInvalid');
    }

    // Validate otherType when OTHER is selected - now optional
    // No validation needed for custom type as it's optional


    // Validate dimensions cannot be negative
    if ((formData.vehicle.dimensions?.length ?? 0) < 0) {
      errors.length = t('pages.devices.validation.lengthCannotBeNegative');
    }

    if ((formData.vehicle.dimensions?.height ?? 0) < 0) {
      errors.height = t('pages.devices.validation.heightCannotBeNegative');
    }

    if ((formData.vehicle.dimensions?.maxLoad ?? 0) < 0) {
      errors.maxLoad = t('pages.devices.validation.maxLoadCannotBeNegative');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, isDirty, t, selectedVehicleTypeCategory]);

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [validateForm]);

  // Security validation effect
  useEffect(() => {
    if (user?.id) {
      validateSecurityAccess();
    }
  }, [user?.id, validateSecurityAccess]);

  const fetchDevice = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await deviceService.getById(id);
      if (response.success) {
        const deviceData = deviceMappers.toFormData(response.data);
        setFormData(deviceData);
        
        // Determine the category based on the vehicle type
        const vehicleType = deviceData.vehicle.type;
        const isPredefinedType = vehicleType && vehicleTypes.includes(vehicleType as VehicleType);
        
        if (isPredefinedType) {
          // If it's a predefined type, set it as the category
          setSelectedVehicleTypeCategory(vehicleType);
        } else if (vehicleType && vehicleType.trim() !== '') {
          // If it's a custom type (not empty), set category to OTHER and keep the custom value
          setSelectedVehicleTypeCategory('OTHER');
          // Keep the custom vehicle type as is
        } else {
          // If it's empty or undefined, set category to OTHER but keep the type empty
          setSelectedVehicleTypeCategory('OTHER');
          // Clear the vehicle type to allow empty custom input
          setFormData(prev => ({
            ...prev,
            vehicle: {
              ...prev.vehicle,
              type: '' as VehicleType,
            },
          }));
        }
      } else {
        setError(response.error.message || t('errors.deviceNotFound'));
      }
    } catch (error) {
      console.error('Error fetching device:', error);
      setError(t('errors.fetchDeviceFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [id, t, vehicleTypes]);

  useEffect(() => {
    // Only fetch device data if security check is complete and we're in edit mode
    if (!isCheckingSecurity && id) {
      fetchDevice();
    }
  }, [isCheckingSecurity, id, fetchDevice]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      const response = id
        ? await deviceService.update(id, formData)
        : await deviceService.create(formData);

      if (response.success) {
        navigate('/devices');
      } else {
        // Handle validation errors from backend
        if (response.error.code.startsWith('DEVICE_VALIDATION_')) {
          const fieldErrors: Record<string, string> = {};
          const errorCode = response.error.code;
          
          // Map error codes to form fields
          const fieldMapping: Record<string, string> = {
            'DEVICE_VALIDATION_SERIAL_REQUIRED': 'serialNumber',
            'DEVICE_VALIDATION_SERIAL_INVALID_FORMAT': 'serialNumber',
            'DEVICE_VALIDATION_VEHICLE_PLATE_FORMAT': 'plate',
            'DEVICE_VALIDATION_VEHICLE_PLATE_TYPE': 'plate',
            'DEVICE_VALIDATION_VEHICLE_LENGTH_INVALID': 'length',
            'DEVICE_VALIDATION_VEHICLE_HEIGHT_INVALID': 'height',
            'DEVICE_VALIDATION_VEHICLE_LOAD_INVALID': 'maxLoad',
            'DEVICE_VALIDATION_VERSION_CONTROLLER_TYPE': 'versions.controller',
            'DEVICE_VALIDATION_VERSION_SCREEN_TYPE': 'versions.screen',
            'DEVICE_VALIDATION_VERSION_SENSOR_TYPE': 'versions.sensor',
            'DEVICE_VALIDATION_ALIAS_TYPE': 'alias',
            'DEVICE_VALIDATION_DESCRIPTION_TYPE': 'description',
            'DEVICE_VALIDATION_STATUS_INVALID': 'status'
          };

          const field = fieldMapping[errorCode] || 'general';
          fieldErrors[field] = t(`validation.device.${errorCode.toLowerCase()}`);
          setValidationErrors(fieldErrors);
          setError(t('errors.formValidation'));
        } else {
          // Check if we have <response.error.code>

          const errorCodeExists = response.error.code != null && response.error.code != '';
          if (errorCodeExists) {
            setError(t(`validation.device.${response.error.code.toLowerCase()}`));
          } else {
            setError(t('errors.saveDeviceFailed') + ' ' + response.error.message);
          }
        }
      }
    } catch (error: unknown) {
      console.error('Error saving device:', error);
      
      // Handle axios errors with validation details
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { 
            data?: { 
              errorDetails?: Array<{ path?: string[]; message?: string }>;
              type?: string;
            } 
          };
        };
        
        // Handle validation errors from middleware
        if (axiosError.response?.data?.type === 'validation' && axiosError.response?.data?.errorDetails) {
          const fieldErrors: Record<string, string> = {};
          axiosError.response.data.errorDetails.forEach((detail) => {
            const fieldName = detail.path?.[0] || 'general';
            fieldErrors[fieldName] = detail.message || t('validation.invalidField');
          });
          setValidationErrors(fieldErrors);
          setError(t('errors.formValidation'));
        } else if (axiosError.response?.data?.errorDetails) {
          // Handle other types of errorDetails
          const fieldErrors: Record<string, string> = {};
          axiosError.response.data.errorDetails.forEach((detail) => {
            const fieldName = detail.path?.[0] || 'general';
            fieldErrors[fieldName] = detail.message || t('validation.invalidField');
          });
          setValidationErrors(fieldErrors);
          setError(t('errors.formValidation'));
        } else {
          setError(t('errors.saveDeviceFailed'));
        }
      } else {
        setError(t('errors.saveDeviceFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    // TODO: Fix this
    // eslint-disable-next-line no-undef
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setIsDirty(true);
    const { name, value, type } = e.target;
    
    // Convertir a mayúsculas si es el campo serialNumber y limpiar caracteres inválidos
    let processedValue = name === 'serialNumber' 
      ? value.toUpperCase().replace(/[^A-Z0-9-]/g, '')
      : value;
      
    // Lógica especial para serialNumber
    if (name === 'serialNumber') {
      // Si el usuario está escribiendo (no retrocediendo) y tiene 6 caracteres sin guión
      if (processedValue.length === 6 && 
          !processedValue.includes('-') &&
          value.length > (formData.serialNumber?.length || 0)) {
        processedValue = processedValue + '-';
      }
      
      // Si tiene más de 6 caracteres sin guión, insertar el guión en la posición 6
      else if (processedValue.length > 6 && !processedValue.includes('-')) {
        processedValue = processedValue.slice(0, 6) + '-' + processedValue.slice(6);
      }
      
      // Si tiene guión pero está en posición incorrecta, corregirlo
      else if (processedValue.includes('-')) {
        const parts = processedValue.split('-');
        if (parts.length === 2) {
          // Asegurar que el guión esté en la posición correcta (después de 6 caracteres)
          const beforeDash = parts[0].slice(0, 6); // Máximo 6 caracteres antes del guión
          const afterDash = parts[1].slice(0, 3);  // Máximo 3 caracteres después del guión
          processedValue = beforeDash + '-' + afterDash;
        } else {
          // Si hay múltiples guiones, mantener solo el primero y reorganizar
          const cleanValue = processedValue.replace(/-/g, '');
          processedValue = cleanValue.slice(0, 6) + '-' + cleanValue.slice(6, 9);
        }
      }
    }
    
    const numValue = type === 'number' ? Number(processedValue) : processedValue;

    // TODO: Fix this
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateNestedState = (obj: any, path: string[], value: any): any => {
      const [current, ...rest] = path;
      if (rest.length === 0) {
        return { ...obj, [current]: value };
      }
      return {
        ...obj,
        [current]: updateNestedState(obj[current] || {}, rest, value),
      };
    };

    const path = name.split('.');
    setFormData((prev) => updateNestedState(prev, path, numValue));
  };

  const handlePermanentDelete = async () => {
    if (!id) return;

    const deviceName = formData.alias || formData.serialNumber || id;
    
    confirmDelete(deviceName, 'device', false, async () => {
      setIsDeleting(true);
      setError(null);

      try {
        const response = await deviceService.delete(id);
        if (response.success) {
          showToast(t('common.devicePermanentlyDeletedSuccess', { name: deviceName }), 'success');
          navigate('/devices');
        } else {
          // Handle specific error cases
          if (response.error.code === 'DEVICE_HAS_LECTURES') {
            showErrorDialog(deviceName, 'device', response.error.code);
          } else if (response.error.code === 'DEVICE_HAS_ASSOCIATIONS') {
            showErrorDialog(deviceName, 'device', response.error.code);
          } else {
            setError(response.error.message || t('common.devicePermanentDeleteError', { name: deviceName }));
          }
        }
      } catch (error) {
        console.error('Error permanently deleting device:', error);
        setError(t('common.devicePermanentDeleteError', { name: deviceName }));
      } finally {
        setIsDeleting(false);
      }
    });
  };

  const handleSequenceResetConfirm = async () => {
    if (!id) return;
    const n = parseInt(String(sequenceInput).trim(), 10);
    if (Number.isNaN(n) || n < 0) {
      showToast(t('pages.devices.sequence.invalidNumber'), 'error');
      return;
    }
    setSequenceSaving(true);
    try {
      const res = await deviceService.patchNextExpectedSequence(id, n);
      if (res.success) {
        const nextVal = res.data.nextExpectedSequence ?? n;
        setFormData(prev => ({ ...prev, nextExpectedSequence: nextVal }));
        showToast(t('pages.devices.sequence.success'), 'success');
        setSequenceResetOpen(false);
      } else {
        showToast(res.error.message || t('pages.devices.sequence.error'), 'error');
      }
    } catch {
      showToast(t('pages.devices.sequence.error'), 'error');
    } finally {
      setSequenceSaving(false);
    }
  };

  // Show loading while checking security or loading device data
  if (isCheckingSecurity || (isLoading && id)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Get current language from i18n
  const currentLanguage = i18n.language === 'es' ? 'es' : 'en';

  return (
    <div className="min-h-screen bg-brand-white p-8">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-brand-orange"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
              {id ? t('common.saving') : t('common.creating')}
            </h3>
            <p className="text-sm text-gray-600 text-center">
              {id ? t('pages.devices.savingDevice') : t('pages.devices.creatingDevice')}
            </p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
          <h1 className="text-3xl font-bold text-brand-black sentence-case">
            {id ? t('pages.devices.editDevice') : t('pages.devices.createNew')}
          </h1>
          {DEVICE_FEATURES.SHOW_SEQUENCE_MANAGER && id && hasPermission(PERMISSIONS.DEVICE_UPDATE) && (
            <button
              type="button"
              onClick={() => {
                const cur = formData.nextExpectedSequence;
                const num =
                  cur != null && !Number.isNaN(Number(cur)) ? Math.floor(Number(cur)) : 0;
                setSequenceInput(String(num));
                setSequenceResetOpen(true);
              }}
              className="bg-brand-white text-brand-orange border border-brand-orange hover:bg-brand-orange hover:text-brand-white px-4 py-2 rounded-md transition-colors duration-200 sentence-case whitespace-nowrap shrink-0"
            >
              {t('pages.devices.sequence.resetButton')}
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-brand-white p-0 rounded-lg shadow-lg space-y-4 border border-gray-200">
            <h2 className="text-xl font-semibold text-brand-white bg-brand-gray-dark p-4 rounded-t-lg">
              {t('pages.devices.sections.basicInfo')}
            </h2>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">
                  {t('pages.devices.fields.serialNumber')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleChange}
                  required
                  pattern="[A-Z0-9\-]{5,50}"
                  placeholder={t('pages.devices.placeholders.serialNumber')}
                  className={`w-full ${
                    !hasPermission(PERMISSIONS.DEVICE_UPDATE_SERIAL_NUMBER) ? 'special-input' : ''
                  }`}
                  disabled={!hasPermission(PERMISSIONS.DEVICE_UPDATE_SERIAL_NUMBER)}
                  maxLength={50}
                />
                {validationErrors.serialNumber ? (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.serialNumber}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    {t('pages.devices.validation.serialNumberFormat')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">
                  {t('pages.devices.fields.alias')}
                </label>
                <input
                  type="text"
                  name="alias"
                  value={formData.alias || ''}
                  onChange={handleChange}
                  placeholder={t('pages.devices.placeholders.alias')}
                  className="w-full"
                />
                {validationErrors.alias && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.alias}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">
                  {t('pages.devices.fields.description')}
                </label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  rows={2}
                  placeholder={t('pages.devices.placeholders.description')}
                  className="w-full form-description"
                />
              </div>
            </div>
          </div>

          {/* Vehicle Information */}
          <div className="bg-brand-white p-0 rounded-lg shadow-lg space-y-4 border border-gray-200">
            <h2 className="text-xl font-semibold text-brand-white bg-brand-gray-dark p-4 rounded-t-lg">
              {t('pages.devices.sections.vehicleInfo')}
            </h2>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">
                  {t('pages.devices.fields.plate')}
                </label>
                <input
                  type="text"
                  name="vehicle.plate"
                  value={formData.vehicle.plate}
                  onChange={handleChange}
                  placeholder={t('pages.devices.placeholders.plate')}
                  className="w-full"
                />
                {validationErrors.plate && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.plate}</p>
                )}
                {!validationErrors.plate && (
                  <p className="mt-1 text-xs text-gray-500">{t('pages.devices.placeholders.plate')}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">
                  {t('pages.devices.fields.vehicleType')}
                </label>
                <select
                  value={selectedVehicleTypeCategory}
                  onChange={(e) => {
                    const selectedCategory = e.target.value;
                    setSelectedVehicleTypeCategory(selectedCategory);
                    
                    if (selectedCategory === 'OTHER') {
                      // When "OTHER" is selected, clear the vehicle type to allow custom input
                      setFormData(prev => ({
                        ...prev,
                        vehicle: {
                          ...prev.vehicle,
                          type: '' as VehicleType,
                        },
                      }));
                    } else {
                      // When a predefined type is selected, set it as the vehicle type and clear the custom type
                      setFormData(prev => ({
                        ...prev,
                        vehicle: {
                          ...prev.vehicle,
                          type: selectedCategory as VehicleType,
                        },
                      }));
                    }
                  }}
                  className="w-full"
                >
                  {vehicleTypes.map((type) => (
                    <option key={type} value={type}>
                      {getVehicleTypeLabel(type, currentLanguage as 'en' | 'es')}
                    </option>
                  ))}
                </select>
                {selectedVehicleTypeCategory === 'OTHER' && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-brand-gray-dark">
                      {t('pages.devices.fields.otherType')}
                    </label>
                    <input
                      type="text"
                      name="vehicle.type"
                      value={formData.vehicle.type}
                      onChange={handleChange}
                      placeholder={t('pages.devices.placeholders.otherType')}
                      className="w-full"
                    />
                    {validationErrors.otherType && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.otherType}</p>
                    )}
                    {!validationErrors.otherType && (
                      <p className="mt-1 text-xs text-gray-500">
                        {t('pages.devices.placeholders.otherTypeOptional')}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.devices.fields.length')}
                  </label>
                  <input
                    type="number"
                    name="vehicle.dimensions.length"
                    value={formData.vehicle.dimensions?.length || 0}
                    onChange={handleChange}
                    placeholder={t('pages.devices.placeholders.length')}
                    className="w-full"
                    min="0"
                    step="0.01"
                  />
                  {validationErrors.length && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.length}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.devices.fields.height')}
                  </label>
                  <input
                    type="number"
                    name="vehicle.dimensions.height"
                    value={formData.vehicle.dimensions?.height || 0}
                    onChange={handleChange}
                    placeholder={t('pages.devices.placeholders.height')}
                    className="w-full"
                    min="0"
                    step="0.01"
                  />
                  {validationErrors.height && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.height}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.devices.fields.maxLoad')}
                  </label>
                  <input
                    type="number"
                    name="vehicle.dimensions.maxLoad"
                    value={formData.vehicle.dimensions?.maxLoad || 0}
                    onChange={handleChange}
                    placeholder={t('pages.devices.placeholders.maxLoad')}
                    className="w-full"
                    min="0"
                    step="0.01"
                  />
                  {validationErrors.maxLoad && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.maxLoad}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* App Versions (requires permission) */}
          {hasPermission(PERMISSIONS.DEVICE_EDIT_APP_VERSIONS) && (
            <div className="bg-brand-white p-0 rounded-lg shadow-lg space-y-4 border border-gray-200">
              <h2 className="text-xl font-semibold text-brand-white bg-brand-gray-dark p-4 rounded-t-lg">
                {t('pages.devices.sections.versions')}
              </h2>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-gray-dark">
                      {t('pages.devices.fields.controllerVersion')}
                    </label>
                    <input
                      type="text"
                      name="versions.controller"
                      value={formData.versions?.controller || ''}
                      onChange={handleChange}
                      placeholder={t('pages.devices.placeholders.controllerVersion')}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-gray-dark">
                      {t('pages.devices.fields.screenVersion')}
                    </label>
                    <input
                      type="text"
                      name="versions.screen"
                      value={formData.versions?.screen || ''}
                      onChange={handleChange}
                      placeholder={t('pages.devices.placeholders.screenVersion')}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-gray-dark">
                      {t('pages.devices.fields.sensorVersion')}
                    </label>
                    <input
                      type="text"
                      name="versions.sensor"
                      value={formData.versions?.sensor || ''}
                      onChange={handleChange}
                      placeholder={t('pages.devices.placeholders.sensorVersion')}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status section */}
          {DEVICE_FEATURES.SHOW_USER_MANAGEMENT && (
            <div className="bg-brand-white p-0 rounded-lg shadow-lg space-y-4 border border-gray-200">
              <h2 className="text-xl font-semibold text-brand-white bg-brand-gray-dark p-4 rounded-t-lg">
                {t('pages.devices.sections.status')}
              </h2>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-gray-dark">
                    {t('pages.devices.fields.status')}
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full"
                  >
                    <option value="Active">{t('pages.devices.status.active')}</option>
                    <option value="Inactive">{t('pages.devices.status.inactive')}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            {/* Permanent Delete Button - Only for Administrators in Edit Mode */}
            {id && hasPermission(PERMISSIONS.DEVICE_DELETE) && (
              <button
                type="button"
                onClick={handlePermanentDelete}
                disabled={isDeleting || isLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200 sentence-case disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? t('common.saving') : t('common.permanentDelete')}
              </button>
            )}
            
            {/* Save and Cancel Buttons */}
            <div className="flex gap-4 ml-auto">
              <button
                type="button"
                onClick={() => navigate('/devices')}
                className="bg-brand-white text-brand-orange border border-brand-orange hover:bg-brand-orange hover:text-brand-white px-4 py-2 rounded-md transition-colors duration-200 sentence-case"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading || !isFormValid}
                className={`bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 sentence-case ${
                  isLoading || !isFormValid ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </form>

        {error && <div className="mt-4 bg-red-50 text-red-600 p-4 rounded-md">{error}</div>}
      </div>

      {DEVICE_FEATURES.SHOW_SEQUENCE_MANAGER && sequenceResetOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl border border-gray-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sequence-reset-title"
          >
            <h3 id="sequence-reset-title" className="text-lg font-semibold text-gray-900 mb-3">
              {t('pages.devices.sequence.dialogTitle')}
            </h3>
            <p className="text-sm text-gray-600 mb-1">{t('pages.devices.sequence.currentLabel')}</p>
            <p className="text-2xl font-mono font-semibold text-brand-gray-dark mb-4">
              {formData.nextExpectedSequence != null ? formData.nextExpectedSequence : '—'}
            </p>
            <label className="block text-sm font-medium text-brand-gray-dark mb-1" htmlFor="sequence-new-input">
              {t('pages.devices.sequence.newLabel')}
            </label>
            <input
              id="sequence-new-input"
              type="number"
              min={0}
              step={1}
              value={sequenceInput}
              onChange={e => setSequenceInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-1 font-mono"
              disabled={sequenceSaving}
            />
            <p className="text-xs text-gray-500 mb-4">{t('pages.devices.sequence.hint')}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSequenceResetOpen(false)}
                disabled={sequenceSaving}
                className="bg-brand-white text-brand-orange border border-brand-orange px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {t('pages.devices.sequence.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSequenceResetConfirm}
                disabled={sequenceSaving}
                className="bg-brand-orange text-white px-4 py-2 rounded-md hover:bg-opacity-90 disabled:opacity-50"
              >
                {sequenceSaving ? t('common.saving') : t('pages.devices.sequence.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
